import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown, Mail, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const topLinks = [{
    name: 'About Us',
    path: '/about'
  }, {
    name: 'Blog',
    path: '#'
  }, {
    name: 'Careers',
    path: '#'
  }, {
    name: 'Depot Locations',
    path: '/contact'
  }];
  const mainNavLinks = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'Boiler Hire',
    path: '/boiler-hire'
  }, {
    name: 'Heater Hire',
    path: '/heater-hire'
  }, {
    name: 'Chiller Hire',
    path: '/chiller-hire'
  }, {
    name: 'Cooling Hire',
    path: '/cooling-hire'
  }, {
    name: 'Ancillaries',
    path: '/ancillaries'
  }, {
    name: 'Industry Sectors',
    path: '/services'
  }];
  const isActive = (path: string) => location.pathname === path;
  return <header className="w-full flex flex-col z-50 relative shadow-sm" data-id="element-118">
      {/* Top Bar */}
      <div className="bg-brand-darker text-gray-300 text-xs py-2 hidden md:block" data-id="element-119">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end space-x-6" data-id="element-120">
          {topLinks.map(link => <Link key={link.name} to={link.path} className="hover:text-brand-green transition-colors" data-id="element-121">
              {link.name}
            </Link>)}
        </div>
      </div>

      {/* Main Header Area */}
      <div className="bg-white py-4" data-id="element-122">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center" data-id="element-123">
          {/* Logo */}
          <Link to="/" className="flex flex-col items-start group" data-id="element-124">
            <div className="flex items-center text-3xl font-heading font-extrabold tracking-tight" data-id="element-125">
              <span className="text-brand-dark" data-id="element-126">RAPID</span>
              <span className="text-brand-green italic ml-1" data-id="element-127">ENERGY</span>
            </div>
            <span className="text-[10px] text-gray-500 uppercase tracking-widest mt-1 font-medium group-hover:text-brand-dark transition-colors" data-id="element-128">
              Your Critical Asset Hire Partner
            </span>
          </Link>

          {/* Desktop Contact Info */}
          <div className="hidden md:flex items-center space-x-6" data-id="element-129">
            <div className="flex items-center text-right" data-id="element-130">
              <div className="bg-brand-green/10 p-3 rounded-full mr-4" data-id="element-131">
                <Phone className="w-6 h-6 text-brand-green" data-id="element-132" />
              </div>
              <div data-id="element-133">
                <a href="tel:08004647025" className="block text-2xl font-heading font-bold text-brand-dark hover:text-brand-green transition-colors" data-id="element-134">
                  0800 464 7025
                </a>
                <span className="text-sm text-gray-500 font-medium" data-id="element-135">
                  Available 24/7
                </span>
              </div>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden p-2 text-brand-dark hover:text-brand-green transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-136">
            {isMobileMenuOpen ? <X className="w-8 h-8" data-id="element-137" /> : <Menu className="w-8 h-8" data-id="element-138" />}
          </button>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="bg-brand-dark hidden md:block" data-id="element-139">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-140">
          <nav className="flex justify-center space-x-8" data-id="element-141">
            {mainNavLinks.map(link => <Link key={link.name} to={link.path} className={`py-4 text-sm font-medium flex items-center transition-colors border-b-2 ${isActive(link.path) ? 'border-brand-green text-brand-green' : 'border-transparent text-white hover:text-brand-green hover:border-brand-green'}`} data-id="element-142">
                {link.name}
              </Link>)}
          </nav>
        </div>
      </div>

      {/* Green CTA Bar */}
      <div className="bg-brand-green text-brand-darker py-3 text-center font-medium text-sm md:text-base px-4" data-id="element-143">
        Call us now on{' '}
        <a href="tel:08004647025" className="font-bold hover:underline" data-id="element-144">
          0800 464 7025
        </a>{' '}
        for a free estimate
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-145">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-brand-dark text-white absolute top-full left-0 w-full shadow-xl overflow-hidden" data-id="element-146">
            <div className="px-4 pt-2 pb-6 space-y-1" data-id="element-147">
              {mainNavLinks.map(link => <Link key={link.name} to={link.path} className="block px-3 py-3 rounded-md text-base font-medium hover:bg-white/10 hover:text-brand-green transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-148">
                  {link.name}
                </Link>)}
              <div className="border-t border-white/10 my-4 pt-4" data-id="element-149">
                {topLinks.map(link => <Link key={link.name} to={link.path} className="block px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:text-white transition-colors" onClick={() => setIsMobileMenuOpen(false)} data-id="element-150">
                    {link.name}
                  </Link>)}
              </div>
              <div className="mt-6 px-3 flex items-center text-brand-green" data-id="element-151">
                <Phone className="w-5 h-5 mr-3" data-id="element-152" />
                <a href="tel:08004647025" className="text-xl font-bold" data-id="element-153">
                  0800 464 7025
                </a>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}